/**
 * Copy an image to the system clipboard so it can be pasted (Ctrl + V) into a WhatsApp Web chat.
 * Chrome's async Clipboard API only accepts `image/png`, so JPEG and the rest go through a canvas.
 */

/** Re-encode any browser-decodable image as PNG. A PNG passes through untouched. */
export async function toPngBlob(blob: Blob): Promise<Blob> {
  if (blob.type === "image/png") return blob;
  const bitmap = await createImageBitmap(blob);
  try {
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("canvas unavailable");
    ctx.drawImage(bitmap, 0, 0);
    return await new Promise<Blob>((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("png encoding failed"))), "image/png"));
  } finally {
    bitmap.close?.();
  }
}

/** True when the browser can write images to the clipboard. */
export function canCopyImages(): boolean {
  return typeof ClipboardItem !== "undefined" && typeof navigator.clipboard?.write === "function";
}

/**
 * Put one image on the clipboard. Resolves `true` on success and `false` when unsupported or
 * refused (permission, no user activation, decoding failure), so the caller can fall back to a download.
 */
export async function copyImageToClipboard(blob: Blob): Promise<boolean> {
  if (!canCopyImages()) return false;
  try {
    // The promise form keeps the tap's user activation alive while the PNG is produced.
    await navigator.clipboard.write([new ClipboardItem({ "image/png": toPngBlob(blob) })]);
    return true;
  } catch {
    return false;
  }
}

/** A touch-first device (a tablet): the OS share sheet is the natural route there, not Ctrl + V. */
export function isTouchDevice(): boolean {
  return navigator.maxTouchPoints > 0 && (typeof matchMedia !== "function" || matchMedia("(pointer: coarse)").matches);
}

/** Whether the OS share sheet accepts image files on this device. */
export function canShareImageFiles(): boolean {
  if (typeof navigator.share !== "function" || typeof navigator.canShare !== "function") return false;
  try {
    return navigator.canShare({ files: [new File([""], "indicaciones.jpg", { type: "image/jpeg" })] });
  } catch {
    return false;
  }
}

/** The share sheet leads only on a touch device that can share files; everywhere else, copy and paste leads. */
export function preferShareSheet(): boolean {
  return isTouchDevice() && canShareImageFiles();
}
